import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { useLanguage } from '../context/LanguageContext';
import { smartMenuAPI, reportsAPI } from '../lib/api';
import { formatCurrency, formatNumber } from '../lib/utils';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { Lightbulb, Star, TrendUp, TrendDown, Minus, Download, Sparkle, Warning, Medal } from '@phosphor-icons/react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';

const recommendationStyles = {
  'Yıldız Ürün': { color: 'bg-yellow-100 text-yellow-700', icon: Star },
  'Fırsat': { color: 'bg-blue-100 text-blue-700', icon: Sparkle },
  'İş Atı': { color: 'bg-orange-100 text-orange-700', icon: Warning },
  'Dikkat': { color: 'bg-red-100 text-red-700', icon: TrendDown },
  'Normal': { color: 'bg-muted text-muted-foreground', icon: Minus },
};

const COLORS = ['#D15E36', '#6E8A72', '#E6BE78', '#C94A4A', '#78746D'];

export default function SmartMenu() {
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30');
  const [exporting, setExporting] = useState(false);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    fetchData();
  }, [period]);

  const fetchData = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await smartMenuAPI.getAnalysis(parseInt(period));
      setMenuItems(res.data);
    } catch (error) {
      const st = error.response?.status;
      setLoadError(error.code === 'ECONNABORTED' ? 'İstek zaman aşımına uğradı (60 sn)' : `Akıllı Menü yüklenemedi (${st ? 'HTTP ' + st : error.message || 'bağlantı hatası'})`);
    } finally {
      setLoading(false);
    }
  };

  const handleExportPDF = async () => {
    setExporting(true);
    try {
      const res = await reportsAPI.exportSmartMenuPDF(parseInt(period));
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `akilli_menu_${new Date().toISOString().slice(0, 10)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF indirildi');
    } catch (error) {
      toast.error('PDF oluşturulamadı');
    } finally {
      setExporting(false);
    }
  };

  const getRecommendationBadge = (recommendation) => {
    const key = Object.keys(recommendationStyles).find(k => recommendation.includes(k)) || 'Normal';
    const style = recommendationStyles[key];
    const Icon = style.icon;
    return (
      <span className={`inline-flex items-center gap-1 badge-pill ${style.color}`}>
        <Icon size={14} weight="fill" />
        {recommendation.split(' - ')[0]}
      </span>
    );
  };

  const chartData = menuItems.slice(0, 10).map(item => ({
    name: item.recipe_name.length > 15 ? item.recipe_name.slice(0, 15) + '...' : item.recipe_name,
    kar: item.profit,
    satis: item.sales_count,
    marj: item.profit_margin,
  }));

  const starProducts = menuItems.filter(m => m.recommendation.includes('Yıldız'));
  const opportunityProducts = menuItems.filter(m => m.recommendation.includes('Fırsat'));
  const warningProducts = menuItems.filter(m => m.recommendation.includes('Dikkat') || m.recommendation.includes('İş Atı'));

  if (loadError && !loading) {
    return (
      <Layout>
        <div className="p-8 space-y-3" data-testid="smart-menu-error">
          <p className="text-destructive">{loadError}</p>
          <Button variant="outline" onClick={fetchData} data-testid="smart-menu-retry">Yeniden Dene</Button>
        </div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight">
              Akıllı Menü
            </h1>
            <p className="mt-2 text-muted-foreground">
              Karlılık ve satış analizine göre menü önerileri
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-40" data-testid="smart-menu-period">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Son 7 Gün</SelectItem>
                <SelectItem value="30">Son 30 Gün</SelectItem>
                <SelectItem value="90">Son 90 Gün</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              onClick={handleExportPDF}
              disabled={exporting}
              data-testid="export-smart-menu-pdf"
            >
              <Download size={18} className="mr-2" />
              {exporting ? 'İndiriliyor...' : 'PDF İndir'}
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="border border-border bg-card p-5 rounded-xl">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 rounded-lg bg-yellow-100">
                <Star size={20} weight="fill" className="text-yellow-600" />
              </div>
              <h3 className="font-heading font-semibold">Yıldız Ürünler</h3>
            </div>
            <p className="text-3xl font-heading font-bold text-yellow-600">{starProducts.length}</p>
            <p className="text-sm text-muted-foreground mt-1">Yüksek kar ve popüler</p>
          </div>
          
          <div className="border border-border bg-card p-5 rounded-xl">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 rounded-lg bg-blue-100">
                <Sparkle size={20} weight="fill" className="text-blue-600" />
              </div>
              <h3 className="font-heading font-semibold">Fırsat Ürünleri</h3>
            </div>
            <p className="text-3xl font-heading font-bold text-blue-600">{opportunityProducts.length}</p>
            <p className="text-sm text-muted-foreground mt-1">Yüksek kar, tanıtım yapın</p>
          </div>
          
          <div className="border border-border bg-card p-5 rounded-xl">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2 rounded-lg bg-red-100">
                <Warning size={20} weight="fill" className="text-red-600" />
              </div>
              <h3 className="font-heading font-semibold">İncelenmeli</h3>
            </div>
            <p className="text-3xl font-heading font-bold text-red-600">{warningProducts.length}</p>
            <p className="text-sm text-muted-foreground mt-1">Düşük kar veya maliyet sorunu</p>
          </div>
        </div>

        {/* Chart */}
        <div className="border border-border bg-card p-6 rounded-xl">
          <h2 className="font-heading text-xl font-semibold mb-6">Kar Marjı Karşılaştırması (İlk 10)</h2>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis type="number" tickFormatter={(v) => `%${v.toFixed(0)}`} />
                <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 11 }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px',
                  }}
                  formatter={(value, name) => [
                    name === 'marj' ? `%${value.toFixed(1)}` : value,
                    name === 'marj' ? 'Kar Marjı' : name === 'satis' ? 'Satış' : 'Kar'
                  ]}
                />
                <Bar dataKey="marj" radius={[0, 4, 4, 0]}>
                  {chartData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={entry.marj >= 50 ? '#6E8A72' : entry.marj >= 30 ? '#E6BE78' : '#C94A4A'}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Detail Table */}
        <div className="border border-border bg-card rounded-xl overflow-hidden">
          <div className="p-4 border-b border-border">
            <h2 className="font-heading text-xl font-semibold">Tüm Ürün Analizi</h2>
          </div>
          <Table className="data-table">
            <TableHeader>
              <TableRow className="border-b border-border bg-muted/30">
                <TableHead className="py-4 w-12">#</TableHead>
                <TableHead>Ürün</TableHead>
                <TableHead>Kategori</TableHead>
                <TableHead className="text-right">Fiyat</TableHead>
                <TableHead className="text-right">Toplam reçete maliyeti</TableHead>
                <TableHead className="text-right">Porsiyon başı maliyet</TableHead>
                <TableHead className="text-right">Kar</TableHead>
                <TableHead className="text-right">Marj</TableHead>
                <TableHead className="text-right">Satış</TableHead>
                <TableHead>Öneri</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {menuItems.map((item, index) => (
                <TableRow key={item.recipe_id} className="table-row-hover border-b border-border/40" data-testid={`smart-menu-row-${item.recipe_id}`}>
                  <TableCell className="py-3">
                    {index < 3 ? (
                      <Medal size={20} weight="fill" className={index === 0 ? 'text-yellow-500' : index === 1 ? 'text-gray-400' : 'text-amber-600'} />
                    ) : (
                      <span className="text-muted-foreground">{index + 1}</span>
                    )}
                  </TableCell>
                  <TableCell className="font-medium">{item.recipe_name}</TableCell>
                  <TableCell className="text-muted-foreground">{item.category}</TableCell>
                  <TableCell className="text-right font-data">{formatCurrency(item.selling_price)}</TableCell>
                  <TableCell className="text-right font-data">{formatCurrency(item.total_cost)}<div className="text-xs text-muted-foreground">{item.portions_per_recipe} porsiyon</div></TableCell>
                  <TableCell className="text-right font-data" data-testid={`smart-menu-cpp-${item.recipe_id}`}>{formatCurrency(item.cost_per_portion)}</TableCell>
                  <TableCell className={`text-right font-data ${item.profit >= 0 ? 'text-secondary' : 'text-destructive'}`}>
                    {formatCurrency(item.profit)}
                  </TableCell>
                  <TableCell className="text-right">
                    <span className={`font-data font-semibold ${item.profit_margin >= 50 ? 'text-secondary' : item.profit_margin >= 30 ? 'text-accent-foreground' : 'text-destructive'}`}>
                      %{formatNumber(item.profit_margin, 1)}
                    </span>
                  </TableCell>
                  <TableCell className="text-right font-data">{item.sales_count}</TableCell>
                  <TableCell>{getRecommendationBadge(item.recommendation)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Tips */}
        <div className="border border-accent/30 bg-accent/10 p-6 rounded-xl">
          <div className="flex items-start gap-3">
            <Lightbulb size={24} weight="duotone" className="text-accent-foreground mt-1" />
            <div>
              <h3 className="font-heading font-semibold mb-2">Akıllı Menü İpuçları</h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>• <strong>Yıldız Ürünler</strong>: Bu ürünleri menüde öne çıkarın ve stokta tutun</li>
                <li>• <strong>Fırsat Ürünleri</strong>: Yüksek karlı ama az satılan - sosyal medya ve garson önerileriyle tanıtın</li>
                <li>• <strong>İş Atları</strong>: Popüler ama düşük karlı - fiyat artışı veya porsiyon optimizasyonu düşünün</li>
                <li>• <strong>Dikkat Edilmesi Gerekenler</strong>: Malzeme maliyetlerini inceleyin veya menüden çıkarmayı düşünün</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
